// biome-ignore-start lint/suspicious/noExplicitAny: serialization needs any
// biome-ignore-start lint/style/noNonNullAssertion: parsing by accessing array at specific index

import {CompressedArrayOfStrings, CompressedScraperItems} from './scraper_item_compression';

// Binary format:
// - Magic: 4 bytes "CSIS" (Compressed Scraper Items Serialized)
// - Version: 1 byte (currently 1)
// - Length: 4 bytes (uint32, number of items)
// - Then all arrays in fixed order (see serialize/deserialize functions)
// - String arrays: length (4 bytes) + UTF-8 bytes
// - Dictionaries: count (4 bytes) + strings

const MAGIC = new TextEncoder().encode('CSIS');
const VERSION = 1;

function writeUint32(view: DataView, offset: number, value: number): number {
  view.setUint32(offset, value, true); // little-endian
  return offset + 4;
}

function readUint32(view: DataView, offset: number): {value: number; offset: number} {
  return {value: view.getUint32(offset, true), offset: offset + 4};
}

function writeInt32Array(view: DataView, offset: number, arr: Int32Array): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  for (let i = 0; i < length; i++) {
    view.setInt32(offset, arr[i]!, true);
    offset += 4;
  }
  return offset;
}

function readInt32Array(view: DataView, offset: number): {arr: Int32Array; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr = new Int32Array(length);
  let o2 = o1;
  for (let i = 0; i < length; i++) {
    arr[i] = view.getInt32(o2, true);
    o2 += 4;
  }
  return {arr, offset: o2};
}

function writeInt16Array(view: DataView, offset: number, arr: Int16Array): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  for (let i = 0; i < length; i++) {
    view.setInt16(offset, arr[i]!, true);
    offset += 2;
  }
  return offset;
}

function readInt16Array(view: DataView, offset: number): {arr: Int16Array; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr = new Int16Array(length);
  let o2 = o1;
  for (let i = 0; i < length; i++) {
    arr[i] = view.getInt16(o2, true);
    o2 += 2;
  }
  return {arr, offset: o2};
}

function writeUint8Array(view: DataView, offset: number, arr: Uint8Array): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  const bytes = new Uint8Array(view.buffer, offset, length);
  bytes.set(arr);
  return offset + length;
}

function readUint8Array(view: DataView, offset: number): {arr: Uint8Array; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr = new Uint8Array(view.buffer, o1, length);
  return {arr, offset: o1 + length};
}

function writeStringArray(
  view: DataView,
  offset: number,
  arr: string[],
  encoder: typeof TextEncoder.prototype
): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  for (let i = 0; i < length; i++) {
    const str = arr[i]!;
    const encoded = encoder.encode(str);
    offset = writeUint32(view, offset, encoded.length);
    const bytes = new Uint8Array(view.buffer, offset, encoded.length);
    bytes.set(encoded);
    offset += encoded.length;
  }
  return offset;
}

function readStringArray(
  view: DataView,
  offset: number,
  decoder: typeof TextDecoder.prototype
): {arr: string[]; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr: string[] = [];
  let o2 = o1;
  for (let i = 0; i < length; i++) {
    const {value: strLength, offset: o3} = readUint32(view, o2);
    o2 = o3;
    const bytes = new Uint8Array(view.buffer, o2, strLength);
    arr.push(decoder.decode(bytes));
    o2 += strLength;
  }
  return {arr, offset: o2};
}

function writeOptionalStringArray(
  view: DataView,
  offset: number,
  arr: (string | undefined)[],
  encoder: typeof TextEncoder.prototype
): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  for (let i = 0; i < length; i++) {
    const str = arr[i];
    if (str === undefined) {
      offset = writeUint32(view, offset, 0);
    } else {
      const encoded = encoder.encode(str);
      offset = writeUint32(view, offset, encoded.length);
      const bytes = new Uint8Array(view.buffer, offset, encoded.length);
      bytes.set(encoded);
      offset += encoded.length;
    }
  }
  return offset;
}

function readOptionalStringArray(
  view: DataView,
  offset: number,
  decoder: typeof TextDecoder.prototype
): {arr: (string | undefined)[]; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr: (string | undefined)[] = [];
  let o2 = o1;
  for (let i = 0; i < length; i++) {
    const {value: strLength, offset: o3} = readUint32(view, o2);
    o2 = o3;
    if (strLength === 0) {
      arr.push(undefined);
    } else {
      const bytes = new Uint8Array(view.buffer, o2, strLength);
      arr.push(decoder.decode(bytes));
      o2 += strLength;
    }
  }
  return {arr, offset: o2};
}

function writeUint8ArrayArray(view: DataView, offset: number, arr: Uint8Array[]): number {
  const length = arr.length;
  offset = writeUint32(view, offset, length);
  for (let i = 0; i < length; i++) {
    const item = arr[i]!;
    offset = writeUint32(view, offset, item.length);
    const bytes = new Uint8Array(view.buffer, offset, item.length);
    bytes.set(item);
    offset += item.length;
  }
  return offset;
}

function readUint8ArrayArray(view: DataView, offset: number): {arr: Uint8Array[]; offset: number} {
  const {value: length, offset: o1} = readUint32(view, offset);
  const arr: Uint8Array[] = [];
  let o2 = o1;
  for (let i = 0; i < length; i++) {
    const {value: itemLength, offset: o3} = readUint32(view, o2);
    o2 = o3;
    const bytes = new Uint8Array(view.buffer, o2, itemLength);
    arr.push(new Uint8Array(bytes));
    o2 += itemLength;
  }
  return {arr, offset: o2};
}

function writeCompressedArrayOfStrings(
  view: DataView,
  offset: number,
  arr: CompressedArrayOfStrings
): number {
  offset = writeInt32Array(view, offset, arr.flat);
  offset = writeInt32Array(view, offset, arr.offsets);
  return offset;
}

function readCompressedArrayOfStrings(
  view: DataView,
  offset: number
): {arr: CompressedArrayOfStrings; offset: number} {
  const {arr: flat, offset: o1} = readInt32Array(view, offset);
  const {arr: offsets, offset: o2} = readInt32Array(view, o1);
  return {arr: {flat, offsets}, offset: o2};
}

function calculateSerializedSize(data: CompressedScraperItems): number {
  const encoder = new TextEncoder();
  let size = 4 + 1 + 4; // magic + version + length

  // Int32Array fields (24 fields)
  size += 4 + data.id.length * 4;
  size += 4 + data.price.length * 4;
  size += 4 + data.areaM2.length * 4;
  size += 4 + data.landSurface.length * 4;
  size += 4 + data.idtiers.length * 4;
  size += 4 + data.agencyId.length * 4;
  size += 4 + data.urlOriginId.length * 4;
  size += 4 + data.title.length * 4;
  size += 4 + data.currency.length * 4;
  size += 4 + data.city.length * 4;
  size += 4 + data.estatePostalCode.length * 4;
  size += 4 + data.clientPostalCode.length * 4;
  size += 4 + data.country.length * 4;
  size += 4 + data.zoneCode.length * 4;
  size += 4 + data.provinceId.length * 4;
  size += 4 + data.estateType.length * 4;
  size += 4 + data.estateSubType.length * 4;
  size += 4 + data.clientType.length * 4;
  size += 4 + data.energyCertificate.length * 4;
  size += 4 + data.promotionType.length * 4;
  size += 4 + data.productType.length * 4;
  size += 4 + data.typeLabel.length * 4;
  size += 4 + data.locationLabel.length * 4;
  size += 4 + data.imagesOriginsFlat.length * 4;
  size += 4 + data.imagesOffsets.length * 4;

  // Int16Array fields (4 fields)
  size += 4 + data.nbRooms.length * 2;
  size += 4 + data.nbBedrooms.length * 2;
  size += 4 + data.floorNb.length * 2;
  size += 4 + data.distributionType.length * 2;

  // Uint8Array fields (3 fields)
  size += 4 + data.parking.length;
  size += 4 + data.urlEncodingType.length;
  size += 4 + data.imagesEncodingType.length;

  // Optional string arrays
  size += 4; // length
  for (const str of data.urlFixedMiddle) {
    size += 4;
    if (str !== undefined) {
      size += encoder.encode(str).length;
    }
  }
  size += 4; // length
  for (const str of data.urlSuffix) {
    size += 4;
    if (str !== undefined) {
      size += encoder.encode(str).length;
    }
  }

  // String arrays
  size += 4; // length
  for (const str of data.imagesFixedMiddles) {
    size += 4 + encoder.encode(str).length;
  }
  size += 4; // length
  for (const str of data.imagesSuffixes) {
    size += 4 + encoder.encode(str).length;
  }

  // Uint8Array arrays
  size += 4; // length
  for (const arr of data.descriptionGzip) {
    size += 4 + arr.length;
  }

  // CompressedArrayOfStrings
  size += 4 + data.productStatusTags.flat.length * 4;
  size += 4 + data.productStatusTags.offsets.length * 4;
  size += 4 + data.mediaTags.flat.length * 4;
  size += 4 + data.mediaTags.offsets.length * 4;

  // Dictionaries
  size += 4; // length
  for (const str of data.strings) {
    size += 4 + encoder.encode(str).length;
  }
  size += 4; // length
  for (const str of data.urlOrigins) {
    size += 4 + encoder.encode(str).length;
  }

  return size;
}

export function serializeCompressedScraperItems(data: CompressedScraperItems): ArrayBuffer {
  const encoder = new TextEncoder();

  // Calculate exact size needed
  const exactSize = calculateSerializedSize(data);
  const buffer = new ArrayBuffer(exactSize);
  const view = new DataView(buffer);
  let offset = 0;

  // Write magic
  new Uint8Array(buffer, offset, 4).set(MAGIC);
  offset += 4;

  // Write version
  view.setUint8(offset, VERSION);
  offset += 1;

  // Write length
  offset = writeUint32(view, offset, data.length);

  // Write all Int32Array fields
  offset = writeInt32Array(view, offset, data.id);
  offset = writeInt32Array(view, offset, data.price);
  offset = writeInt32Array(view, offset, data.areaM2);
  offset = writeInt32Array(view, offset, data.landSurface);
  offset = writeInt32Array(view, offset, data.idtiers);
  offset = writeInt32Array(view, offset, data.agencyId);
  offset = writeInt32Array(view, offset, data.urlOriginId);
  offset = writeInt32Array(view, offset, data.title);
  offset = writeInt32Array(view, offset, data.currency);
  offset = writeInt32Array(view, offset, data.city);
  offset = writeInt32Array(view, offset, data.estatePostalCode);
  offset = writeInt32Array(view, offset, data.clientPostalCode);
  offset = writeInt32Array(view, offset, data.country);
  offset = writeInt32Array(view, offset, data.zoneCode);
  offset = writeInt32Array(view, offset, data.provinceId);
  offset = writeInt32Array(view, offset, data.estateType);
  offset = writeInt32Array(view, offset, data.estateSubType);
  offset = writeInt32Array(view, offset, data.clientType);
  offset = writeInt32Array(view, offset, data.energyCertificate);
  offset = writeInt32Array(view, offset, data.promotionType);
  offset = writeInt32Array(view, offset, data.productType);
  offset = writeInt32Array(view, offset, data.typeLabel);
  offset = writeInt32Array(view, offset, data.locationLabel);
  offset = writeInt32Array(view, offset, data.imagesOriginsFlat);
  offset = writeInt32Array(view, offset, data.imagesOffsets);

  // Write all Int16Array fields
  offset = writeInt16Array(view, offset, data.nbRooms);
  offset = writeInt16Array(view, offset, data.nbBedrooms);
  offset = writeInt16Array(view, offset, data.floorNb);
  offset = writeInt16Array(view, offset, data.distributionType);

  // Write all Uint8Array fields
  offset = writeUint8Array(view, offset, data.parking);
  offset = writeUint8Array(view, offset, data.urlEncodingType);
  offset = writeUint8Array(view, offset, data.imagesEncodingType);

  // Write string arrays (optional)
  offset = writeOptionalStringArray(view, offset, data.urlFixedMiddle, encoder);
  offset = writeOptionalStringArray(view, offset, data.urlSuffix, encoder);
  offset = writeStringArray(view, offset, data.imagesFixedMiddles, encoder);
  offset = writeStringArray(view, offset, data.imagesSuffixes, encoder);

  // Write Uint8Array arrays
  offset = writeUint8ArrayArray(view, offset, data.descriptionGzip);

  // Write CompressedArrayOfStrings
  offset = writeCompressedArrayOfStrings(view, offset, data.productStatusTags);
  offset = writeCompressedArrayOfStrings(view, offset, data.mediaTags);

  // Write dictionaries
  offset = writeStringArray(view, offset, data.strings, encoder);
  offset = writeStringArray(view, offset, data.urlOrigins, encoder);

  // Return trimmed buffer
  return buffer.slice(0, offset);
}

export function deserializeCompressedScraperItems(buffer: ArrayBuffer): CompressedScraperItems {
  const view = new DataView(buffer);
  const decoder = new TextDecoder();
  let offset = 0;

  // Read and verify magic
  const magic = new Uint8Array(buffer, offset, 4);
  if (
    magic[0] !== MAGIC[0] ||
    magic[1] !== MAGIC[1] ||
    magic[2] !== MAGIC[2] ||
    magic[3] !== MAGIC[3]
  ) {
    throw new Error('Invalid magic bytes');
  }
  offset += 4;

  // Read version
  const version = view.getUint8(offset);
  if (version !== VERSION) {
    throw new Error(`Unsupported version: ${version}`);
  }
  offset += 1;

  // Read length
  const {value: length, offset: o1} = readUint32(view, offset);
  offset = o1;

  // Read all Int32Array fields
  const {arr: id, offset: o2} = readInt32Array(view, offset);
  offset = o2;
  const {arr: price, offset: o3} = readInt32Array(view, offset);
  offset = o3;
  const {arr: areaM2, offset: o4} = readInt32Array(view, offset);
  offset = o4;
  const {arr: landSurface, offset: o5} = readInt32Array(view, offset);
  offset = o5;
  const {arr: idtiers, offset: o6} = readInt32Array(view, offset);
  offset = o6;
  const {arr: agencyId, offset: o7} = readInt32Array(view, offset);
  offset = o7;
  const {arr: urlOriginId, offset: o8} = readInt32Array(view, offset);
  offset = o8;
  const {arr: title, offset: o9} = readInt32Array(view, offset);
  offset = o9;
  const {arr: currency, offset: o10} = readInt32Array(view, offset);
  offset = o10;
  const {arr: city, offset: o11} = readInt32Array(view, offset);
  offset = o11;
  const {arr: estatePostalCode, offset: o12} = readInt32Array(view, offset);
  offset = o12;
  const {arr: clientPostalCode, offset: o13} = readInt32Array(view, offset);
  offset = o13;
  const {arr: country, offset: o14} = readInt32Array(view, offset);
  offset = o14;
  const {arr: zoneCode, offset: o15} = readInt32Array(view, offset);
  offset = o15;
  const {arr: provinceId, offset: o16} = readInt32Array(view, offset);
  offset = o16;
  const {arr: estateType, offset: o17} = readInt32Array(view, offset);
  offset = o17;
  const {arr: estateSubType, offset: o18} = readInt32Array(view, offset);
  offset = o18;
  const {arr: clientType, offset: o19} = readInt32Array(view, offset);
  offset = o19;
  const {arr: energyCertificate, offset: o20} = readInt32Array(view, offset);
  offset = o20;
  const {arr: promotionType, offset: o21} = readInt32Array(view, offset);
  offset = o21;
  const {arr: productType, offset: o22} = readInt32Array(view, offset);
  offset = o22;
  const {arr: typeLabel, offset: o23} = readInt32Array(view, offset);
  offset = o23;
  const {arr: locationLabel, offset: o24} = readInt32Array(view, offset);
  offset = o24;
  const {arr: imagesOriginsFlat, offset: o25} = readInt32Array(view, offset);
  offset = o25;
  const {arr: imagesOffsets, offset: o26} = readInt32Array(view, offset);
  offset = o26;

  // Read all Int16Array fields
  const {arr: nbRooms, offset: o27} = readInt16Array(view, offset);
  offset = o27;
  const {arr: nbBedrooms, offset: o28} = readInt16Array(view, offset);
  offset = o28;
  const {arr: floorNb, offset: o29} = readInt16Array(view, offset);
  offset = o29;
  const {arr: distributionType, offset: o30} = readInt16Array(view, offset);
  offset = o30;

  // Read all Uint8Array fields
  const {arr: parking, offset: o31} = readUint8Array(view, offset);
  offset = o31;
  const {arr: urlEncodingType, offset: o32} = readUint8Array(view, offset);
  offset = o32;
  const {arr: imagesEncodingType, offset: o33} = readUint8Array(view, offset);
  offset = o33;

  // Read string arrays (optional)
  const {arr: urlFixedMiddle, offset: o34} = readOptionalStringArray(view, offset, decoder);
  offset = o34;
  const {arr: urlSuffix, offset: o35} = readOptionalStringArray(view, offset, decoder);
  offset = o35;
  const {arr: imagesFixedMiddles, offset: o36} = readStringArray(view, offset, decoder);
  offset = o36;
  const {arr: imagesSuffixes, offset: o37} = readStringArray(view, offset, decoder);
  offset = o37;

  // Read Uint8Array arrays
  const {arr: descriptionGzip, offset: o38} = readUint8ArrayArray(view, offset);
  offset = o38;

  // Read CompressedArrayOfStrings
  const {arr: productStatusTags, offset: o39} = readCompressedArrayOfStrings(view, offset);
  offset = o39;
  const {arr: mediaTags, offset: o40} = readCompressedArrayOfStrings(view, offset);
  offset = o40;

  // Read dictionaries
  const {arr: strings, offset: o41} = readStringArray(view, offset, decoder);
  offset = o41;
  const {arr: urlOrigins, offset: o42} = readStringArray(view, offset, decoder);
  offset = o42;

  return {
    length,
    id,
    price,
    areaM2,
    landSurface,
    nbRooms,
    nbBedrooms,
    floorNb,
    distributionType,
    idtiers,
    agencyId,
    parking,
    urlEncodingType,
    urlFixedMiddle,
    urlOriginId,
    urlSuffix,
    descriptionGzip,
    title,
    currency,
    city,
    estatePostalCode,
    clientPostalCode,
    country,
    zoneCode,
    provinceId,
    estateType,
    estateSubType,
    clientType,
    energyCertificate,
    promotionType,
    productType,
    typeLabel,
    locationLabel,
    imagesEncodingType,
    imagesFixedMiddles,
    imagesOriginsFlat,
    imagesSuffixes,
    imagesOffsets,
    productStatusTags,
    mediaTags,
    strings,
    urlOrigins,
  };
}

// biome-ignore-end lint/style/noNonNullAssertion: parsing by accessing array at specific index
// biome-ignore-end lint/suspicious/noExplicitAny: serialization needs any
